import React, { createContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext();

/* ================= ENV VARIABLES ================= */
export const base_url = process.env.REACT_APP_API_URL;
export const app_url = process.env.REACT_APP_APP_URL;
export const AGENTID = process.env.REACT_APP_AGENT_ID;
export const AGENTKEY = process.env.REACT_APP_AGENT_KEY;
export const REFERRERURL = process.env.REACT_APP_REFERRER_URL;
export const BASEURL = process.env.REACT_APP_BASE_URL;

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [userInfo, setUserInfo] = useState(null);
  const [userBalnc, setUserBalnc] = useState({
    userTotalBal: 0,
    bonus: 0,
  });

  const isFetchingUser = useRef(false);
  const inactivityTimer = useRef(null);

  /* ================= CONSTANTS ================= */
  const INACTIVITY_TIME = 14 * 60 * 1000; // 14 minutes

  const PaymentStatusMap = { PENDING: 0, SUCCESS: 1, CANCELLED: 2 };
  const WITHDRAWAL_STATUS_MAP = { PENDING: 0, APPROVED: 1, DENIED: 2 };

  /* ================= COOKIE HELPERS ================= */
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? match[2] : null;
  };

  const loginstatus = getCookie("auth");
  const [isAuthenticated, setIsAuthenticated] = useState(!!loginstatus);

  /* ================= INACTIVITY HANDLING ================= */
  const clearAuth = () => {
    document.cookie = "token=; max-age=0; path=/;";
    document.cookie = "auth=; max-age=0; path=/;";
    setIsAuthenticated(false);
    setUserInfo(null);
    setUserBalnc({ userTotalBal: 0, bonus: 0 });
  };

  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      clearAuth();
      navigate("/login", { replace: true });
    }, INACTIVITY_TIME);
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach((event) =>
      document.addEventListener(event, resetInactivityTimer)
    );

    resetInactivityTimer();

    return () => {
      events.forEach((event) =>
        document.removeEventListener(event, resetInactivityTimer)
      );
      clearTimeout(inactivityTimer.current);
    };
  }, [isAuthenticated]);

  /* ================= REGISTER ================= */
  const Register = async (data, callback) => {
    try {
      const res = await axios.post(
        `${base_url}/api/webapi/register`,
        data,
        { headers: { Accept: "application/json" } }
      );
      callback(res.data);
    } catch (error) {
      callback({
        status: false,
        message: error.response?.data?.message || error.message,
      });
    }
  };

  /* ================= LOGIN ================= */
  const login = async (data, callback) => {
    try {
      const res = await axios.post(
        `${base_url}/api/webapi/login`,
        data,
        {
          withCredentials: true,
          headers: { Accept: "application/json" },
        }
      );

      if (!res.data.status) {
        toast.error(res.data.message);
        return;
      }

      isFetchingUser.current = false;

      const expiry = new Date(Date.now() + 60 * 60 * 1000).toUTCString();
      document.cookie = `token=${res.data.token}; expires=${expiry}; path=/;`;
      document.cookie = `auth=${res.data.value}; expires=${expiry}; path=/;`;

      setIsAuthenticated(true);
      resetInactivityTimer();

      callback(res.data);
    } catch (error) {
      callback({
        status: false,
        message: error.response?.data?.message || error.message,
      });
    }
  };

  /* ================= USER DATA ================= */
  const userdata = async () => {
    if (isFetchingUser.current) return null;
    isFetchingUser.current = true;

    try {
      const res = await axios.post(
        `${base_url}/api/webapi/GetUserInfo`,
        {},
        {
          withCredentials: true,
          timeout: 8000,
          headers: { Accept: "application/json" },
        }
      );

      const data = res.data?.data;
      // console.log("Fetched user data:", data);

      setUserBalnc({
        userTotalBal: data.money_user,
        bonus: res.data.bonus_money || 0,
      });

      setUserInfo({
        inviteCode: data.code,
        totalLogin: data.login_count,
        nameUser: data.name_user,
        userPhone: data.phone_user,
        userName: data.username,
      });

      return res.data;
    } catch (err) {
      console.error("User fetch error:", err.message);
      return null;
    } finally {
      isFetchingUser.current = false;
    }
  };
useEffect(() => {
  if (isAuthenticated) {
    userdata();
  }
}, [isAuthenticated]);

  /* ================= CHANGE PASSWORD ================= */
  const changepassword = async (data, callback) => {
    try {
      const res = await axios.post(
        `${base_url}/api/webapi/change/pass`,
        { ...data, auth: loginstatus }
      );
      callback(res.data);
    } catch (err) {
      callback({
        status: false,
        message: err.response?.data?.message || err.message,
      });
    }
  };



  /* ================= LOGOUT ================= */
  const logout = () => {
    clearAuth();
    isFetchingUser.current = false;
    localStorage.removeItem("hasSeenLoginModal");
    
    navigate("/login", { replace: true });
    window.location.reload();
  };

  /* ================= BANK DETAILS ================= */
  const getBankDetails = async () => {
    try {
      const res = await axios.post(
        `${base_url}/admin/manager/settings/get`,
        {},
        {
          withCredentials: true,
          headers: { Accept: "application/json" },
        }
      );
      return res.data.momo;
    } catch (err) {
      console.error("Bank fetch error:", err.message);
      return null;
    }
  };

  /* ================= ROLES ================= */
   const getRoles = async (auth, role) => {
    try {
      const response = await axios.post(base_url + '/api/webapi/admin/Rolelist',
        {},
        {
          headers: {
            Accept: 'application/json',
            Authorization: `${auth}`
          }
        }
      )

      const fullRoles = response.data.data;
      // console.log('full',fullRoles);

      const specifiedRole = fullRoles.filter((oneRole) => Object.keys(oneRole).includes(role));

      return specifiedRole;
    } catch (err) {
      console.log('err', err);
    }
  }

  /* ================= PROVIDER ================= */
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        PaymentStatusMap,
        WITHDRAWAL_STATUS_MAP,
        Register,
        login,
        logout,
        userdata,
        userBalnc,
        setUserBalnc,
        changepassword,
        getBankDetails,
        setIsAuthenticated,
        getRoles,
        base_url,
        userInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
